// Dictionary, 852 part 2 — the LIN (item-activity) loop's own segments: LIN
// itself and QTY, the quantity-report segment. Generic X12 852 (5010)
// definitions only.
//
// CTP, REF, PER, DTM, PID, SDQ and the nested N1-N4 Name sub-loop all recur
// inside this loop too, but X12 defines one segment per id — they're already
// defined in dictionary/header-segments.ts and dictionary/item-loop-
// segments.ts and merged in by index.ts, so they are not redefined here.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {PRODUCT_SERVICE_ID_QUALIFIER_CODES} from './product-service-id-qualifiers';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const ITEM_ACTIVITY_SEGMENTS_852: Record<string, SegmentDefinition> = {
  LIN: {
    id: 'LIN',
    name: 'Item Identification',
    purpose:
      'Opens one item-activity loop: identifies the item this report line is about. Every item-activity loop starts with a LIN.',
    loop: LoopId.Item,
    required: true,
    elements: [
      el(
        1,
        'Assigned Identification',
        'The line number, assigned by the reporting party.'
      ),
      el(
        2,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows.',
        {
          dataType: 'code',
          required: true,
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(
        3,
        'Product/Service ID',
        'The item identifier, in the scheme named by element 2.',
        {required: true}
      ),
      el(
        4,
        'Product/Service ID Qualifier',
        'A second item identifier scheme, when the item carries more than one id.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(5, 'Product/Service ID', 'The second item identifier.'),
    ],
  },

  QTY: {
    id: 'QTY',
    name: 'Quantity Information',
    purpose:
      'Reports one quantity for the item-activity line — units on hand, units sold, units received, etc. An item-activity line typically carries more than one QTY, one per kind of quantity being reported.',
    loop: LoopId.Item,
    required: true,
    elements: [
      el(1, 'Quantity Qualifier', 'What this quantity represents.', {
        dataType: 'code',
        required: true,
        codes: [
          {code: '48', label: 'Units on Hand (Inventory)'},
          {code: '49', label: 'Units Sold (Sales)'},
          {code: 'ON', label: 'Units on Order'},
          {code: 'X6', label: 'Units Received'},
          {code: 'ZZ', label: 'Mutually Defined'},
        ],
      }),
      el(2, 'Quantity', 'The quantity value.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        3,
        'Unit or Basis for Measurement Code',
        'The unit the quantity is expressed in.',
        {
          dataType: 'code',
          codes: [
            {code: 'EA', label: 'Each'},
            {code: 'CA', label: 'Case'},
            {code: 'DZ', label: 'Dozen'},
          ],
        }
      ),
      el(
        4,
        'Free-Form Message',
        'Optional free-form note about this quantity.'
      ),
    ],
  },
};
